/**
 * Governance UI kit — the shared, reusable foundation every governance
 * report is built on (header, scope badge, KPI deck, filter bar, table
 * badges, detail drawer, empty/loading/error states, pagination, CSV
 * download). Adding a new report should mean composing these, not
 * rebuilding the pattern.
 */
import React from "react";
import { Download, FileText, ArrowLeft, X, Inbox, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

/* ── Scope badge ─────────────────────────────────────────────── */
export function ScopeBadge({ user, scope }) {
    const label = scope || (
        user?.role === "super_admin" ? "Super Admin · Platform-wide"
            : user?.role === "org_admin" ? "Org Admin · Organization-wide"
                : "Scoped · Tenant"
    );
    return (
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold border border-border rounded-sm px-2.5 py-1"
            data-testid="gov-scope-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))]" />
            {label}
        </span>
    );
}

/* ── Freshness formatter ─────────────────────────────────────── */
export function freshness(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return String(iso); }
}

/* ── Report header ───────────────────────────────────────────── */
export function ReportHeader({ eyebrow, title, accent, description, generatedAt, freshnessNote, onBack, actions }) {
    return (
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div>
                {onBack && (
                    <button onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-2" data-testid="gov-back">
                        <ArrowLeft className="h-3.5 w-3.5" /> Governance
                    </button>
                )}
                {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
                <h1 className="text-2xl font-semibold tracking-tight" data-testid="gov-report-title">
                    {title} {accent && <span className="font-serif-display italic font-normal">{accent}</span>}
                </h1>
                {description && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{description}</p>}
                {(generatedAt || freshnessNote) && (
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))]" />
                        {freshnessNote || `Generated ${freshness(generatedAt)}`}
                    </div>
                )}
            </div>
            {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
        </div>
    );
}

/* ── CSV export button (auth-safe blob download) ─────────────── */
export function ExportButtons({ csvPath, csvName, pdf = true }) {
    const [busy, setBusy] = React.useState(false);
    const download = async () => {
        setBusy(true);
        try {
            const res = await api.get(csvPath, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = csvName; a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Export failed. Please try again.");
        } finally { setBusy(false); }
    };
    return (
        <>
            <Button variant="outline" className="rounded-sm gap-1.5" onClick={download} disabled={busy} data-testid="gov-export-csv">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} CSV
            </Button>
            {pdf && (
                <Button variant="outline" className="rounded-sm gap-1.5 text-muted-foreground"
                    onClick={() => toast.info("PDF export coming soon", { description: "For now, use CSV or print this page to PDF from your browser. Server-side PDF is on the roadmap." })}
                    data-testid="gov-export-pdf">
                    <FileText className="h-3.5 w-3.5" /> PDF
                    <span className="text-[8.5px] uppercase tracking-wider border border-border rounded-sm px-1 ml-0.5">soon</span>
                </Button>
            )}
        </>
    );
}

/* ── KPI deck ────────────────────────────────────────────────── */
export function KpiDeck({ items, testid = "gov-kpi-deck" }) {
    return (
        <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))` }} data-testid={testid}>
            {items.map((k) => (
                <div key={k.label}
                    className={`bg-card border rounded-sm p-3.5 ${k.tone === "alert" ? "border-[hsl(var(--destructive))]/35" : k.tone === "warn" ? "border-[hsl(var(--warning))]/50" : "border-border"}`}
                    data-testid={`gov-kpi-${k.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <div className="eyebrow !text-[10px] !tracking-[0.14em]">{k.label}</div>
                    {k.notCaptured ? (
                        <div className="text-sm text-muted-foreground italic mt-2" title={k.note}>Not tracked</div>
                    ) : (
                        <div className="kpi-number text-2xl font-semibold mt-2">{k.value}</div>
                    )}
                    {k.sub && <div className={`text-[11px] mt-1 ${k.subTone === "up" ? "text-[hsl(var(--success))]" : k.subTone === "down" ? "text-[hsl(var(--destructive))]" : "text-muted-foreground"}`}>{k.sub}</div>}
                </div>
            ))}
        </div>
    );
}

/* ── Filter bar ──────────────────────────────────────────────── */
export function FilterBar({ children }) {
    return <div className="bg-card border border-border rounded-sm p-3 flex gap-3 items-end flex-wrap mb-4" data-testid="gov-filter-bar">{children}</div>;
}
export function Field({ label, children, grow }) {
    return (
        <div className={`flex flex-col gap-1.5 ${grow ? "flex-1 min-w-[180px]" : ""}`}>
            <label className="eyebrow !text-[10px]">{label}</label>
            {children}
        </div>
    );
}
export const selectCls = "h-9 px-2.5 border border-border rounded-sm bg-background text-[13px] min-w-[130px]";
export const inputCls = "h-9 px-2.5 border border-border rounded-sm bg-background text-[13px] w-full";

/* ── Badges ──────────────────────────────────────────────────── */
const base = "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-semibold whitespace-nowrap";
export function RiskBadge({ risk }) {
    const map = {
        high: ["bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]", "High"],
        review: ["bg-[hsl(var(--warning))]/15 text-[hsl(38_90%_32%)]", "Review"],
        low: ["bg-[hsl(var(--success))]/12 text-[hsl(160_60%_28%)]", "Low"],
    };
    const [cls, label] = map[risk] || map.low;
    return <span className={`${base} ${cls}`} data-testid={`gov-risk-${risk}`}>{label}</span>;
}
export function StatusBadge({ active }) {
    return active
        ? <span className={`${base} bg-[hsl(var(--success))]/12 text-[hsl(160_60%_28%)]`}><span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))]" />Active</span>
        : <span className={`${base} bg-muted text-muted-foreground`}><span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />Inactive</span>;
}
export function RoleBadge({ role }) {
    const priv = role === "super_admin" || role === "org_admin";
    const cls = priv ? "bg-foreground text-background"
        : role === "entity_admin" ? "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]"
            : "bg-muted text-foreground";
    const label = { super_admin: "Super Admin", org_admin: "Org Admin", entity_admin: "Entity Admin", entity_viewer: "Entity Viewer", viewer: "Viewer" }[role] || role;
    return <span className={`${base} ${cls}`}>{label}</span>;
}
export function AuthBadge({ method, provider, locked }) {
    if (method === "sso") {
        const label = provider === "google" ? "SSO · Google" : provider === "microsoft" ? "SSO · Entra" : "SSO";
        return <span className={`${base} bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]`}>{label}</span>;
    }
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`${base} bg-[hsl(var(--warning))]/15 text-[hsl(38_90%_32%)]`}>Password</span>
            {locked && <span className={`${base} bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]`}>Locked</span>}
        </span>
    );
}
export function Pill({ children, tone }) {
    const cls = tone === "info" ? "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]"
        : tone === "ok" ? "bg-[hsl(var(--success))]/12 text-[hsl(160_60%_28%)]"
            : "bg-muted text-foreground";
    return <span className={`${base} ${cls}`}>{children}</span>;
}

/* ── Avatar ──────────────────────────────────────────────────── */
export function Avatar({ name, email }) {
    const initials = (name || email || "?").split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
    return <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-bold shrink-0">{initials}</span>;
}

/* ── States ──────────────────────────────────────────────────── */
export function EmptyState({ title = "Nothing to show", hint }) {
    return (
        <div className="py-14 text-center text-muted-foreground" data-testid="gov-empty">
            <Inbox className="h-8 w-8 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
            <div className="text-sm text-foreground font-medium">{title}</div>
            {hint && <div className="text-xs mt-1">{hint}</div>}
        </div>
    );
}
export function LoadingState() {
    return <div className="py-14 text-center text-muted-foreground" data-testid="gov-loading"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>;
}
export function ErrorState({ onRetry }) {
    return (
        <div className="py-14 text-center text-muted-foreground" data-testid="gov-error">
            <AlertTriangle className="h-8 w-8 mx-auto mb-3 text-[hsl(var(--warning))]" strokeWidth={1.5} />
            <div className="text-sm text-foreground font-medium">Couldn't load this report</div>
            {onRetry && <Button variant="outline" className="rounded-sm mt-3" onClick={onRetry} data-testid="gov-retry">Try again</Button>}
        </div>
    );
}

/* ── Pagination ──────────────────────────────────────────────── */
export function Pagination({ page, totalPages, total, onPage, unit = "rows" }) {
    if (!total) return null;
    return (
        <div className="flex items-center justify-between px-4 py-2.5 bg-muted border-t border-border text-xs text-muted-foreground" data-testid="gov-pagination">
            <span>{total} {unit}{totalPages > 1 ? ` · page ${page}/${totalPages}` : ""}</span>
            {totalPages > 1 && (
                <div className="flex gap-1">
                    <button className="w-7 h-7 border border-border rounded-sm bg-background disabled:opacity-40" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="gov-page-prev">‹</button>
                    <button className="w-7 h-7 border border-border rounded-sm bg-background disabled:opacity-40" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="gov-page-next">›</button>
                </div>
            )}
        </div>
    );
}

/* ── Detail drawer ───────────────────────────────────────────── */
export function DetailDrawer({ open, onClose, eyebrow, title, subtitle, rows = [], footer }) {
    return (
        <>
            <div className={`fixed inset-0 bg-black/25 z-40 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={onClose} />
            <aside className={`fixed top-0 right-0 h-screen w-[440px] max-w-[92vw] bg-card border-l border-border z-50 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "translate-x-full"}`}
                data-testid="gov-detail-drawer">
                <div className="flex items-start justify-between p-5 border-b border-border">
                    <div>
                        {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
                        <h3 className="text-base font-semibold">{title}</h3>
                        {subtitle && <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{subtitle}</div>}
                    </div>
                    <button className="w-8 h-8 border border-border rounded-sm text-muted-foreground hover:text-foreground" onClick={onClose} data-testid="gov-drawer-close"><X className="h-4 w-4 mx-auto" /></button>
                </div>
                <div className="p-5 overflow-y-auto flex-1">
                    {rows.map((r, i) => (
                        <div key={i} className="grid grid-cols-[120px_1fr] gap-3 py-2.5 border-b border-border last:border-0 text-[13px]">
                            <div className="eyebrow !text-[10px] pt-0.5">{r.k}</div>
                            <div className={`font-medium ${r.na ? "!font-normal text-muted-foreground italic" : ""}`}>{r.v}</div>
                        </div>
                    ))}
                </div>
                {footer && <div className="p-4 border-t border-border flex gap-2">{footer}</div>}
            </aside>
        </>
    );
}

/* ── Last-login formatter ────────────────────────────────────── */
export function lastLoginLabel(iso, days) {
    if (!iso) return { text: "Never", muted: true };
    if (days === 0) return { text: "Today", muted: false };
    if (days === 1) return { text: "Yesterday", muted: false };
    if (days != null) return { text: `${days}d ago`, muted: days >= 90 };
    return { text: freshness(iso), muted: false };
}
